import { domainMap } from 'config/env'

import { TIER_KEY, TIER_NAME, TIER_PRICE, TIER_PRICE_ID } from 'app-constants'
import { BillingFrequency, ProductKey, TierGroup, TierOption, TierPrice } from 'generated/types'
import { getFlow } from 'utils/FlowProvider/utils'
import removeDuplicateQueryParams from 'utils/sourceAttributes'

import allTierOptions from './gql/allTierOptionsInTierGroup.query.gql'

export const setTierPriceIdCookie = (id: string) => {
  window.sessionStorage.setItem(TIER_PRICE_ID, id)
  return (document.cookie = `${TIER_PRICE_ID}=${id}; domain=${domainMap.get(
    process.env.BUILD_ENV,
  )}; path=/ `)
}

export const getTierPriceId = (): string => {
  const tierPriceIdFromQueryString =
    removeDuplicateQueryParams(location.search)?.tierPriceId ?? null
  const tierPriceIdFromCookies = document.cookie
    .split(';')
    .filter(cookie => cookie.includes(`${TIER_PRICE_ID}`))
    .toString()
    .split('=')
    .pop()
  if (tierPriceIdFromQueryString) {
    setTierPriceIdCookie(tierPriceIdFromQueryString)
    return tierPriceIdFromQueryString
  } else if (tierPriceIdFromCookies) {
    return tierPriceIdFromCookies
  }

  return null
}

export const getTierPrice = (): number | null => {
  const tierPrice = window.sessionStorage.getItem(TIER_PRICE)
  return tierPrice ? parseInt(tierPrice) : null
}

export const setTierPrice = (tierPrice: number) => {
  return tierPrice && window.sessionStorage.setItem(TIER_PRICE, tierPrice.toString())
}

export const setTierName = (tierName: string) => {
  return tierName && window.sessionStorage.setItem(TIER_NAME, tierName)
}

export const setTierKey = (tierKey: string) => {
  return tierKey && window.sessionStorage.setItem(TIER_KEY, tierKey)
}

export const getTierKey = (): string | null => {
  return window.sessionStorage.getItem(TIER_KEY) || null
}

export const getProductKey = (): ProductKey | null => {
  const product =
    getFlow()
      .charAt(0)
      .toUpperCase() +
    getFlow()
      .slice(1)
      .replace(/onboarding/i, '')

  return ProductKey[product] ?? null
}

export const getTierPriceFromOption = (tierOption: any): TierPrice | null => {
  if (tierOption.data?.tierOptionByProductKey) {
    return tierOption.data.tierOptionByProductKey.preferredTierPrice ?? null
  } else if (tierOption.data?.tierOptionByTierPriceId) {
    return tierOption.data.tierOptionByTierPriceId.preferredTierPrice ?? null
  }
  return null
}

export const getTierNameFromOption = (tierOption: any): string | null => {
  if (tierOption.data?.tierOptionByTierPriceId) {
    return tierOption.data.tierOptionByTierPriceId.tier?.name ?? null
  }
  return null
}

export function getTierKeyFromQueryString(): string | null {
  return removeDuplicateQueryParams(location.search)?.tierKey ?? null
}

export async function getTierPriceIdByTierKey(tierKey, client): Promise<string | undefined> {
  try {
    return await getTierPriceIdUsingGivenTierKey(tierKey, client)
  } catch (error) {
    return
  }
}

async function getTierPriceIdUsingGivenTierKey(tierKey, client): Promise<string | undefined> {
  const tierOption = await getTierOption(tierKey, client)
  if (tierOption) {
    return getTierPriceIdFromTierOption(tierOption)
  }

  return
}

async function getTierOption(tierKey, client): Promise<TierOption | null> {
  const tierOptions = await getTierOptions(client)
  if (tierOptions) {
    return getTierOptionFromTierOptions(tierKey, tierOptions)
  }
  return null
}

function getTierPriceIdFromTierOption(option): string | null {
  const monthlyTierPrice = option.tierPrices.find(
    price => price.billingFrequency === BillingFrequency.Monthly,
  )
  return monthlyTierPrice?.id ?? null
}

function getTierOptionFromTierOptions(tierKey, tierOptions): TierOption | null {
  return tierOptions.find(option => option.tier.key === tierKey) ?? null
}

async function getTierOptions(client): Promise<TierOption[] | null> {
  const response = await client.query({ query: allTierOptions })
  const tierGroup = getTierGroup(response)
  if (tierGroup) {
    return getTierOptionsFromTierGroup(tierGroup)
  }
  return null
}

function getTierGroup(response): TierGroup | null {
  return response?.data?.tierGroup ?? null
}

function getTierOptionsFromTierGroup(tierGroup): TierOption[] | null {
  return tierGroup.tierOptions?.length ? tierGroup.tierOptions : null
}
